import { useEffect } from 'react'
import { AssetSelect } from '@/components/AssetSelect/AssetSelect'
import { IndicationSelect } from '@/components/IndicationSelect/IndicationSelect'
import { KiqMultiSelect } from '@/components/KiqMultiSelect/KiqMultiSelect'
import { InsightTypePills } from '@/components/InsightTypePills/InsightTypePills'
import { RecordButton } from '@/components/RecordButton/RecordButton'
import { Waveform } from '@/components/Waveform/Waveform'
import { PipToggle } from '@/components/PipToggle/PipToggle'
import { NoteInput } from '@/components/NoteInput/NoteInput'
import { OfflineBanner, MicPermissionBanner, UpdateAvailableBanner } from '@/components/Banners/RecorderBanners'
import { RecordingBanner } from '@/components/Banners/RecordingBanner'
import { InstallPrompt } from '@/components/Banners/InstallPrompt'
import { verifyIndexedDBAvailable, verifyMediaRecorderAvailable } from '@/lib/offline'
import { validateSubmission } from '@/lib/validators'
import { newSubmissionId } from '@/lib/ids'
import { extractHashtags } from '@/lib/hashtags'
import { saveSubmission } from '@/lib/db'
import { syncSubmissionImmediately } from '@/lib/sync'
import { useStore } from '@/stores'
import { useToast } from '@/components/providers/ToastProvider'
import { useTaxonomyStore } from '@/stores/taxonomyStore'
import { getLocalTimeString } from '@/lib/time'
import type { Submission } from '@/types'
import { useState } from 'react'
import { AuthStatusIndicator } from '@/components/auth/AuthStatusIndicator'
import { useAuthStateSnapshot } from '@/lib/authStatus'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { UserMenu } from '@/components/UserMenu/UserMenu'
import { RefreshButton } from '@/components/RefreshButton/RefreshButton'
import logoVsai from '../../logo_vsai.png'
import { migrateAudioBlobsForSafari } from '@/lib/migrations/migrateAudioBlobs'
import { attachAudioDebugTools } from '@/lib/debugAudio'
import { normalizeTaxonomySelection } from '@/lib/taxonomySelection'
import { SyncStatusIndicator } from '@/components/SyncStatus/SyncStatusIndicator'
import '@/lib/debugBlobs' // Initialize debug helpers

export function RecorderPage() {
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const selectedAssetId = useStore((state) => state.selectedAssetId)
  const selectedIndicationId = useStore((state) => state.selectedIndicationId)
  const selectedKiqIds = useStore((state) => state.selectedKiqIds)
  const selectedInsightTags = useStore((state) => state.selectedInsightTags)
  const addSubmission = useStore((state) => state.addSubmission)
  const { showToast } = useToast()
  const initTaxonomy = useTaxonomyStore((state) => state.init)
  const refreshTaxonomy = useTaxonomyStore((state) => state.refresh)
  const taxonomyData = useTaxonomyStore((state) => state.data)
  const taxonomyStatus = useTaxonomyStore((state) => ({
    status: state.status,
    error: state.error,
    hasData: Boolean(state.data),
  }))
  const authState = useAuthStateSnapshot()
  const online = useOnlineStatus()

  // Verify offline capabilities on mount
  useEffect(() => {
    initTaxonomy()
    verifyIndexedDBAvailable().then((available) => {
      if (!available) {
        console.warn('IndexedDB not available - offline storage will not work')
      }
    })

    if (!verifyMediaRecorderAvailable()) {
      console.warn('MediaRecorder not available - recording will not work')
    }

    void migrateAudioBlobsForSafari().catch((error) => {
      console.warn('Audio migration failed', error)
    })

    attachAudioDebugTools()
  }, [])

  useEffect(() => {
    if (authState.isAuthenticated && online) {
      void refreshTaxonomy().catch((error) => {
        console.error('Failed to refresh taxonomy after auth', error)
      })
    }
  }, [authState.isAuthenticated, online, refreshTaxonomy])

  const handleNoteSend = async (noteText: string) => {
    const taxonomySelection = normalizeTaxonomySelection({
      assetId: selectedAssetId,
      indicationId: selectedIndicationId,
      kiqIds: selectedKiqIds,
    })

    // Create submission from note
    // Store local browser time (consistent with audio recordings)
    // Format includes seconds for accurate sorting: MM/DD/YYYY HH:MM:SS
    const localTime = getLocalTimeString()
    const submission: Submission = {
      id: newSubmissionId(),
      type: 'text',
      userId: authState.userSub,
      assetId: taxonomySelection.assetId,
      indicationId: taxonomySelection.indicationId,
      kiqIds: taxonomySelection.kiqIds,
      insightTags: selectedInsightTags,
      status: 'saved',
      createdAt: localTime, // Store local browser time as MM/DD/YYYY HH:MM:SS
      updatedAt: localTime,
      noteBody: noteText,
      textPreview: noteText.substring(0, 200),
      tags: extractHashtags(noteText),
      syncedAt: null,
    }

    // Validate submission
    const validation = validateSubmission(submission, taxonomyData)
    if (!validation.valid) {
      showToast(validation.errors[0] || 'Validation failed', 'error')
      return
    }

    try {
      // Save to IndexedDB
      await saveSubmission(submission)
      addSubmission(submission)
      showToast('Note saved successfully', 'success')
      
      // Try to sync to backend immediately (non-blocking)
      syncSubmissionImmediately(submission).catch((error) => {
        console.log('Background sync will handle this submission:', error)
      })
      
      // Dispatch event to refresh recording list
      window.dispatchEvent(new Event('recording-saved'))
    } catch (error) {
      console.error('Failed to save note:', error)
      showToast('Failed to save note. Please try again.', 'error')
    }
  }

  return (
    <div className="bg-navy text-white">
      <header className="flex items-center justify-center gap-3 px-4 pt-4 pb-4 max-w-md mx-auto w-full">
        <img
          src={logoVsai}
          alt="VSAI logo"
          className="h-auto w-48 object-contain"
        />
      </header>
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <UpdateAvailableBanner />
      <RecordingBanner />
      <OfflineBanner />
      <MicPermissionBanner />

      <main
        id="main-content"
        className="px-4 py-6 max-w-md mx-auto w-full pb-24"
        role="main"
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex-1">
            <AuthStatusIndicator />
          </div>
          <RefreshButton />
          <UserMenu />
        </div>
        {/* Install Prompt - Shows after user interaction */}
        <InstallPrompt />

        {/* Selection Controls - Stacked vertically like in image */}
        <div className="space-y-3 mb-6">
          {taxonomyStatus.error && (
            <div
              className="rounded-xl border border-red-500/40 bg-red-950/40 px-4 py-3 text-sm text-red-200"
              role="alert"
            >
              {taxonomyStatus.error}
            </div>
          )}
          <AssetSelect />
          <IndicationSelect />
          <KiqMultiSelect />
          <InsightTypePills />
        </div>

        {/* Waveform Section */}
        <div className="mb-8">
          <Waveform isRecording={isRecording} audioStream={audioStream} />
        </div>

        {/* Record Button - Centered */}
        <div className="flex flex-col items-center gap-2 mb-6">
          <RecordButton
            onStreamChange={setAudioStream}
            onRecordingChange={setIsRecording}
          />
          <PipToggle />
          <SyncStatusIndicator />
        </div>

        {/* Note Input Section */}
        <section className="bg-navy-light rounded-lg p-6 mb-6">
          <NoteInput onSend={handleNoteSend} />
        </section>
      </main>
    </div>
  )
}
